import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Divider,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import AppConfig from "appConfig";
import usePageTitle from "app/pageTitle";

function WaccNumbersPage() {
  const pageTitle = "WhatsApp Numbers";
  usePageTitle(pageTitle);

  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const [numbers, setNumbers] = useState([]);
  const [search, setSearch] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get("agents/wacc/numbers.json")
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            setNumbers(result.data.data.numbers);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  const filteredNumbers = numbers.filter((number) => {
    const value = search.toLowerCase();

    return (
      number.business?.toLowerCase().includes(value) ||
      number.phone_number?.toLowerCase().includes(value) ||
      number.account?.name?.toLowerCase().includes(value)
    );
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <MDBox mt={1} mb={5}>
        <MDBox mb={3}>
          <Card
            sx={{
              overflow: "hidden",
            }}
          >
            <MDBox
              p={2.5}
              sx={{
                background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
              }}
            >
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2.5}
              >
                <MDBox display="flex" alignItems="center">
                  <MDBox
                    sx={{
                      width: "46px",
                      height: "46px",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#e8f5e9",
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: "25px !important",
                      }}
                    >
                      forum
                    </Icon>
                  </MDBox>

                  <MDBox ml={1.5}>
                    <MDTypography variant="h6" fontWeight="medium">
                      WhatsApp Workspace
                    </MDTypography>

                    <MDTypography variant="body2" color="text" fontSize={11}>
                      Manage your conversations with customers
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox
                  sx={{
                    minWidth: "36px",
                    height: "36px",
                    px: 1,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f0f2f5",
                  }}
                >
                  <MDTypography variant="caption" fontWeight="medium">
                    {numbers.length}
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDInput
                fullWidth
                label="Search numbers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </MDBox>
          </Card>
        </MDBox>

        {numbers.length > 0 ? (
          filteredNumbers.length > 0 ? (
            <MDBox>
              {filteredNumbers.map((number) => (
                <MDBox
                  mb={2}
                  key={number.reference}
                  onClick={() => {
                    navigate(`/wacc/numbers/${number.reference}`);
                  }}
                  sx={{
                    cursor: "pointer",
                  }}
                >
                  <Card
                    sx={{
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 5px 18px rgba(0,0,0,0.10)",
                      },
                    }}
                  >
                    <MDBox p={2}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <MDBox display="flex" alignItems="center" minWidth={0}>
                          <MDBox
                            sx={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "10px",
                              backgroundColor: "#f5f5f5",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            {number.account?.icon ? (
                              <img
                                src={`${AppConfig.api_media_url}${number.account.icon}`}
                                alt={number.account.name}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <Icon>business</Icon>
                            )}
                          </MDBox>

                          <MDBox ml={1.5} minWidth={0}>
                            <MDTypography
                              variant="caption"
                              color="text"
                              fontWeight="regular"
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {number.account?.name}
                            </MDTypography>

                            <MDTypography
                              variant="h6"
                              fontSize={16}
                              fontWeight="medium"
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {number.business}
                            </MDTypography>
                          </MDBox>
                        </MDBox>

                        <Icon
                          sx={{
                            fontSize: "22px !important",
                            ml: 2,
                          }}
                        >
                          east
                        </Icon>
                      </MDBox>

                      <Divider sx={{ my: 1.5 }} />

                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <MDBox>
                          <MDTypography variant="caption" color="text">
                            WhatsApp number
                          </MDTypography>

                          <MDTypography
                            variant="button"
                            fontWeight="medium"
                            display="block"
                          >
                            {number.phone_number}
                          </MDTypography>
                        </MDBox>

                        <MDBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            py: 0.5,
                            borderRadius: "6px",
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          <Icon
                            sx={{
                              fontSize: "15px !important",
                              mr: 0.5,
                            }}
                          >
                            forum
                          </Icon>

                          <MDTypography variant="caption" fontWeight="medium">
                            Open Workspace
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
              ))}
            </MDBox>
          ) : (
            <Card>
              <MDBox align="center" p={3}>
                <MDTypography fontSize={42}>
                  <Icon>search_off</Icon>
                </MDTypography>

                <MDTypography variant="body2">
                  No WhatsApp numbers match your search.
                </MDTypography>
              </MDBox>
            </Card>
          )
        ) : (
          <Card>
            <MDBox align="center" p={3}>
              <MDTypography fontSize={48}>
                <Icon>warning</Icon>
              </MDTypography>

              <MDTypography variant="body2">
                You have not been assigned to any WhatsApp numbers yet.
              </MDTypography>
            </MDBox>
          </Card>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default WaccNumbersPage;
